import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Layers, CheckCircle, Plus, X, Factory, Pickaxe, Building, HardHat, UtensilsCrossed, ShoppingBag } from "lucide-react";

const MODULE_ICONS = {
  MINING: Pickaxe, CONSTRUCTION: HardHat, MANUFACTURING: Factory,
  REAL_ESTATE: Building, HOSPITALITY: UtensilsCrossed, RETAIL: ShoppingBag,
};

export default function IndustryModulesPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/industry-modules"); setModules(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activate = async (code) => {
    try {
      const { data } = await api.post("/industry-modules/activate", { module_code: code });
      alert(`Module activated! ${data.extra_categories?.length || 0} extra categories added: ${data.extra_categories?.join(", ") || "none"}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const deactivate = async (code) => {
    if (!window.confirm("Deactivate this module?")) return;
    try {
      await api.post("/industry-modules/deactivate", { module_code: code });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="industry-modules-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
          <Layers size={24} className="inline mr-2 text-[#C8A961]" />
          Industry Modules
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">Activate industry-specific fields, workflows, categories, and reports per entity</p>
      </div>

      {loading ? (
        <div className="stratum-card p-8 text-center text-[#6B7280]">Loading modules...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => {
            const Icon = MODULE_ICONS[m.code] || Layers;
            return (
              <div key={m.code} className={`stratum-card p-6 transition-all ${m.is_active ? "border-l-4 border-l-[#065F46] bg-[#065F46]/5" : ""}`} data-testid={`module-${m.code}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.is_active ? "bg-[#065F46]/10" : "bg-[#C8A961]/10"}`}>
                      <Icon size={20} className={m.is_active ? "text-[#065F46]" : "text-[#C8A961]"} />
                    </div>
                    <div>
                      <h3 className="font-display text-base font-semibold text-[#0B1020]">{m.name}</h3>
                      {m.is_active && <span className="text-xs text-[#065F46] font-medium">Active</span>}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-[#6B7280] mb-3 leading-relaxed">{m.description}</p>
                
                {m.extra_categories?.length > 0 && (
                  <div className="mb-3">
                    <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">Extra Categories</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {m.extra_categories.map((c, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-[#FAF8F4] border border-[#E8E2D5] text-[10px] text-[#1A1A1A]">{c}</span>
                      ))}
                    </div>
                  </div>
                )}

                {m.reports?.length > 0 && (
                  <div className="mb-3">
                    <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">Module Reports</span>
                    <ul className="mt-1 space-y-0.5">
                      {m.reports.map((r, i) => (
                        <li key={i} className="text-xs text-[#1A1A1A]">- {r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {m.is_active ? (
                  <button onClick={() => deactivate(m.code)} className="px-3 py-1.5 text-xs rounded-lg border border-[#991B1B]/30 text-[#991B1B] hover:bg-[#991B1B]/10 flex items-center gap-1.5" data-testid={`deactivate-${m.code}`}>
                    <X size={12} /> Deactivate
                  </button>
                ) : (
                  <button onClick={() => activate(m.code)} className="px-3 py-1.5 text-xs rounded-lg bg-[#0B1020] text-white hover:bg-[#1A233A] flex items-center gap-1.5" data-testid={`activate-${m.code}`}>
                    <Plus size={12} /> Activate Module
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
